/**
 * @fileoverview Display formatters for contact info.
 *
 * Names and phones are stored normalized in the DB (names lowercase, phones
 * E.164 — see backend/src/utils/normalize.ts). These helpers present them:
 *  - titleCase:  "nathan perjak" → "Nathan Perjak"
 *  - formatPhone: "+15738802597" → "(573) 880-2597"
 *  - formatDate:  ISO string → "M/D/YYYY", parsed deterministically (never
 *    relies on `new Date(...)`, which can yield "Invalid Date" on Hermes).
 */

/** Title-case a person's name for display. Capitalizes after space, hyphen, apostrophe. */
export function titleCase(name: string | null | undefined): string {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/(^|[\s'’\-])([a-z])/g, (_m, sep, ch) => sep + ch.toUpperCase());
}

/** Pretty-print a phone number. US/E.164 → "(XXX) XXX-XXXX"; otherwise as-is. */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  let local = digits;
  if (digits.length === 11 && digits.startsWith('1')) local = digits.slice(1);
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return String(raw);
}

/**
 * Format an ISO timestamp as "M/D/YYYY" using its date parts directly.
 * Deterministic across web (V8) and native (Hermes) — does not call
 * `new Date()`, so a valid ISO string can never render as "Invalid Date".
 * Returns the em dash for null/empty/unparseable input.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return '—';
  return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
}
